<template>
    <div>
        <DRow>
            <DCol>
                <DTabs class="mb-3">
                    <DTabItem
                        id="sepa-retry-tab-failure-codes"
                        :label="__('settings.sepa_retry.tabs.failure_codes')"
                        :route-url="$route('settings.sepa-retry-failure-codes.index')"
                        :active="false"
                    />
                    <DTabItem
                        id="sepa-retry-tab-retry-settings"
                        :label="__('settings.sepa_retry.tabs.retry_settings')"
                        :route-url="$route('settings.manage-sepa-retry.create')"
                        :active="true"
                    />
                </DTabs>
            </DCol>
        </DRow>

        <DCard
            :title="__('settings.sepa_retry.title')"
            :subtitle="__('settings.sepa_retry.next_retry_in_days_hint')"
            padding
        >
            <DTable
                :items="tableItems"
                :fields="fields"
            >
                <template #retry_sequence="{ item }">
                    <td>{{ __('settings.sepa_retry.retry', { number: item.index + 1 }) }}</td>
                </template>
                <template #next_retry_in_days="{ item }">
                    <td>
                        <DInput
                            :id="`retry_days_${item.index}`"
                            v-model="form.retries[item.index].days"
                            type="number"
                            min="1"
                            :invalid-feedback="errors[`retries.${item.index}.days`]"
                        />
                    </td>
                </template>
                <template #after="{ item }">
                    <td>
                        <template v-if="item.index === 0">
                            {{ __('settings.sepa_retry.failed_payment') }}
                        </template>
                        <template v-else>
                            {{ __('settings.sepa_retry.retry', { number: item.index }) }}
                        </template>
                    </td>
                </template>
                <template #actions="{ item }">
                    <td>
                        <div
                            v-if="item.index === form.retries.length - 1"
                            class="d-flex gap-2"
                        >
                            <DButton
                                :id="`retry_add_${item.index}`"
                                icon="plus"
                                color="success"
                                mini
                                @click="addRow"
                            />
                            <DButton
                                v-if="form.retries.length > 1"
                                :id="`retry_delete_${item.index}`"
                                icon="trash-can-outline"
                                color="danger"
                                mini
                                @click="confirmRemoveRow"
                            />
                        </div>
                    </td>
                </template>
            </DTable>
        </DCard>

        <DFormControls
            :cancel-route="$route('settings.dunning-settings.create')"
            :dirty="form.isDirty"
            @submit="submit"
        />

        <DModal
            v-model:show="showDeleteConfirm"
            :title="__('settings.sepa_retry.delete_confirm_modal.title')"
            :message="__('settings.sepa_retry.delete_confirm_modal.message')"
            confirm-button-text="settings.sepa_retry.delete_confirm_modal.confirm_button"
            is-confirm
            is-warning
            @close="showDeleteConfirm = false"
            @confirm="removeRow"
        />
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    retries: {
        type: Array,
        default () {
            return []
        }
    },
    errors: {
        type: Object,
        default () {
            return {}
        }
    }
})

const showDeleteConfirm = ref(false)

const form = useForm({
    retries: props.retries.length > 0
        ? props.retries.map(r => ({ days: r.days }))
        : [{ days: null }]
})

const fields = [
    { key: 'retry_sequence', label: __('settings.sepa_retry.retry_sequence'), sorter: false, filter: false },
    { key: 'next_retry_in_days', label: __('settings.sepa_retry.next_retry_in_days'), sorter: false, filter: false },
    { key: 'after', label: __('settings.sepa_retry.after'), sorter: false, filter: false },
    { key: 'actions', label: __('settings.sepa_retry.actions'), sorter: false, filter: false }
]

const tableItems = computed(() => form.retries.map((_, index) => ({ id: index, index })))

function addRow () {
    form.retries.push({ days: null })
}

function confirmRemoveRow () {
    showDeleteConfirm.value = true
}

function removeRow () {
    form.retries.pop()
    showDeleteConfirm.value = false
}

function submit () {
    form.post(route('settings.manage-sepa-retry.store'))
}
</script>

<style lang="scss" scoped>
:deep(.d-modal .actions button) {
    white-space: nowrap;
}
</style>
